import React, {useContext, useEffect, useRef, useState} from 'react'
import {useStyles} from "./GeneralEditPlaylistForm.css";
import FormControl from "@mui/material/FormControl";
import {FormHelperText, Grid, InputAdornment, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import useGeneralEditPlaylistForm from "./useGeneralEditPlaylistForm";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import ScoredSceneCardDetailed from "../../videosScored/card/scoredSceneDetailed/ScoredSceneCardDetailed";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {useIntl} from "react-intl";
import {FactCheck, School, Science, Warning} from "@mui/icons-material";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import AssociationLogo from "../../../../commons/utils/logo/AssociationLogo";

import hash from "object-hash";
import {Prompt} from 'react-router'
import Autocomplete from '@mui/material/Autocomplete';
import FilterCriteriaChip from "../../statistics/chip/FilterCriteriaChip";
import useSelectablePlaylistFilters from "./useSelectablePlaylistFilters";

const GeneralEditPlaylistForm = ({selectedPlaylist, editedPlaylistRef, setSaveButtonEnabled}) => {
    const classes = useStyles()

    // Get permission-based limited association structure / Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    const {
        scoredScenesSource,
        scoredScenesDestination,
        loading,
        association,
        name,
        handleChangeName,
        feedbackMode,
        handleChangeFeedbackMode,
        difficultySearch,
        handleChangeDifficultySearch,
        mainFocusSearch,
        handleChangeMainFocusSearch,
        disciplineSearch,
        handleChangeDisciplineSearch,
        associationSearch,
        handleChangeAssociationSearch,
        handleDragEnd,
        getPlaylistDuration
    } = useGeneralEditPlaylistForm(selectedPlaylist, editedPlaylistRef)

    const {
        getSelectableAssociationItems,
        getSelectableDisciplineItems,
        getSelectableMainFocusItems,
        getSelectableDifficultyItems
    } = useSelectablePlaylistFilters()

    // Internationalization for simple strings
    const intl = useIntl();

    // Handling for hinting the user to save changes in case he tries to leave the form after making changes
    const initialHash = useRef(null)
    const [hasFormChanged, setHasFormChanged] = useState(false)

    useEffect(() => {
        setSaveButtonEnabled(isOperationOnObjectAllowed(selectedPlaylist) && name && association && scoredScenesDestination?.length > 0 && !isMixedAssociations())

        // Refresh the current hash and set the initial hash at the very beginning
        const currentHash = hash(editedPlaylistRef.current, {unorderedArrays: true})
        if (!initialHash.current) {
            initialHash.current = currentHash
        }
        setHasFormChanged(initialHash.current !== currentHash)

    }, [name, association, scoredScenesDestination, feedbackMode])

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const isMixedAssociations = () => {
        if ([0, 1].includes(scoredScenesDestination.length)) {
            return false
        }

        for (let i = 0; i < scoredScenesDestination.length - 1; i++) {
            const scoredScene = scoredScenesDestination[i]
            const nextScoredScene = scoredScenesDestination[i + 1]

            if (scoredScene.video.basics.association !== nextScoredScene.video.basics.association) {
                return true
            }
        }
        return false
    }

    const getFilteredScoredScenesSource = () => {
        return scoredScenesSource.filter((scoredScene) => {
            return (associationSearch.find((a) => a.id === scoredScene.video.basics.association) || associationSearch.length === 0)
                && (disciplineSearch.find((d) => d.id === scoredScene.video.basics.discipline) || disciplineSearch.length === 0)
                && (difficultySearch.find((d) => d.id === scoredScene.basics.difficulty) || difficultySearch.length === 0)
                && (mainFocusSearch.find((m) => scoredScene.basics.mainFocus.includes(m.id)) || mainFocusSearch.length === 0)
        })
    }

    // Show loading indication while loading the list of selectable scored scenes
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.playlists.edit.selectableScoredScenes.loading',
                defaultMessage: 'Loading selectable video scenes...'
            })}/>
    }

    return (
        <fieldset disabled={!isOperationOnObjectAllowed(selectedPlaylist)} style={{borderWidth: 0}}>
            {/* Security prompt if the user tries to (maybe unintendendly) leave the page.
                This needs to be effective if there are changes made to the current object. */}
            <Prompt
                when={hasFormChanged}
                message={intl.formatMessage({
                    id: 'app.utils.unsavedChanges.hint',
                    defaultMessage: 'Your changes are not saved, are you sure you want to leave?'
                })}
            />

            <DragDropContext onDragEnd={handleDragEnd}>

                <Grid container spacing={2}>
                    <Grid item xs={6}>

                        {/* First search row */}
                        <div className={classes.row}>

                            {/* Associations [{id, name}] */}
                            <FormControl className={classes.searchFormControl}>
                                <Autocomplete
                                    multiple
                                    limitTags={2}
                                    id="reg-statistics-associations-input"
                                    options={getSelectableAssociationItems()}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(association) =>
                                        intl.formatMessage({
                                            id: `app.ids.association.${association.id}`,
                                            defaultMessage: association.id
                                        })}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                                   variant="standard"
                                                   label={associationSearch?.length > 0
                                                       ? intl.formatMessage({
                                                           id: 'app.playlists.filter.form.associations.label.filtered',
                                                           defaultMessage: 'Associations'
                                                       })
                                                       : intl.formatMessage({
                                                           id: 'app.playlists.filter.form.associations.label.unfiltered',
                                                           defaultMessage: 'All Associations'
                                                       })}
                                                   placeholder={
                                                       intl.formatMessage({
                                                           id: 'app.playlists.filter.form.associations.placeholder',
                                                           defaultMessage: 'Associations'
                                                       })}/>
                                    )}
                                    renderTags={(tValue, getTagProps) =>
                                        <FilterCriteriaChip filteredValues={associationSearch}
                                                            selectableValues={getSelectableAssociationItems()}
                                                            getTagProps={getTagProps}
                                                            translationPrefix='app.ids.association'/>}
                                    onChange={handleChangeAssociationSearch}
                                    value={associationSearch}
                                />
                            </FormControl>

                            {/* Disciplines [{id, name}] */}
                            <FormControl className={classes.searchFormControl}>
                                <Autocomplete
                                    multiple
                                    limitTags={2}
                                    id="reg-statistics-discipline-input"
                                    options={getSelectableDisciplineItems(associationSearch)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(discipline) =>
                                        intl.formatMessage({
                                            id: `app.ids.discipline.${discipline.id}`,
                                            defaultMessage: discipline.id
                                        })}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                                   variant="standard"
                                                   label={disciplineSearch?.length > 0
                                                       ? intl.formatMessage({
                                                           id: 'app.playlists.filter.form.disciplines.label.filtered',
                                                           defaultMessage: 'Disciplines'
                                                       })
                                                       : intl.formatMessage({
                                                           id: 'app.playlists.filter.form.disciplines.label.unfiltered',
                                                           defaultMessage: 'All Disciplines'
                                                       })}
                                                   placeholder={
                                                       intl.formatMessage({
                                                           id: 'app.playlists.filter.form.disciplines.placeholder',
                                                           defaultMessage: 'Disciplines'
                                                       })}/>
                                    )}
                                    renderTags={(tValue, getTagProps) =>
                                        <FilterCriteriaChip filteredValues={disciplineSearch}
                                                            selectableValues={getSelectableDisciplineItems(associationSearch)}
                                                            getTagProps={getTagProps}
                                                            translationPrefix='app.ids.discipline'/>}
                                    onChange={handleChangeDisciplineSearch}
                                    value={disciplineSearch}
                                />
                            </FormControl>
                        </div>

                        {/* Second search row */}
                        <div className={classes.row}>
                            {/* Main Focus [{id, name}] */}
                            <FormControl className={classes.searchFormControl}>
                                <Autocomplete
                                    multiple
                                    limitTags={2}
                                    id="reg-statistics-mainFocus-input"
                                    options={getSelectableMainFocusItems(associationSearch, disciplineSearch)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(mainFocus) => mainFocus.id}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                                   variant="standard"
                                                   label={mainFocusSearch?.length > 0
                                                       ? intl.formatMessage({
                                                           id: 'app.playlists.filter.form.mainFocus.label.filtered',
                                                           defaultMessage: 'Main Focuses'
                                                       })
                                                       : intl.formatMessage({
                                                           id: 'app.playlists.filter.form.mainFocus.label.unfiltered',
                                                           defaultMessage: 'All Main Focuses'
                                                       })}
                                                   placeholder={
                                                       intl.formatMessage({
                                                           id: 'app.playlists.filter.form.mainFocus.placeholder',
                                                           defaultMessage: 'Main Focuses'
                                                       })}/>
                                    )}
                                    renderTags={(tValue, getTagProps) =>
                                        <FilterCriteriaChip filteredValues={mainFocusSearch}
                                                            selectableValues={getSelectableMainFocusItems(associationSearch, disciplineSearch)}
                                                            getTagProps={getTagProps}/>}
                                    onChange={handleChangeMainFocusSearch}
                                    value={mainFocusSearch}
                                />
                            </FormControl>

                            {/* Difficulty [{id, name}] */}
                            <FormControl className={classes.searchFormControl}>
                                <Autocomplete
                                    multiple
                                    limitTags={2}
                                    id="reg-statistics-difficulty-input"
                                    options={getSelectableDifficultyItems()}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(difficulty) =>
                                        intl.formatMessage({
                                            id: `app.ids.difficulty.${difficulty.id}`,
                                            defaultMessage: difficulty.id
                                        })}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                                   variant="standard"
                                                   label={difficultySearch?.length > 0
                                                       ? intl.formatMessage({
                                                           id: 'app.playlists.filter.form.difficulty.label.filtered',
                                                           defaultMessage: 'Difficulties'
                                                       })
                                                       : intl.formatMessage({
                                                           id: 'app.playlists.filter.form.difficulty.label.unfiltered',
                                                           defaultMessage: 'All Difficulties'
                                                       })}
                                                   placeholder={
                                                       intl.formatMessage({
                                                           id: 'app.playlists.filter.form.difficulty.placeholder',
                                                           defaultMessage: 'Difficulties'
                                                       })}/>
                                    )}
                                    renderTags={(tValue, getTagProps) =>
                                        <FilterCriteriaChip filteredValues={difficultySearch}
                                                            selectableValues={getSelectableDifficultyItems()}
                                                            getTagProps={getTagProps}
                                                            translationPrefix='app.ids.difficulty'/>}
                                    onChange={handleChangeDifficultySearch}
                                    value={difficultySearch}
                                />
                            </FormControl>
                        </div>
                    </Grid>
                    <Grid item xs={6}>
                        {isMixedAssociations()
                            ? <div
                                className={classes.errorContainer}>
                                <Warning className={classes.warningIcon}/>
                                <div>
                                    <div>
                                        {intl.formatMessage({
                                            id: 'app.playlists.messages.drag.mixedAssociationsNotAllowed',
                                            defaultMessage: 'Mixed association in a playlist are not allowed.'
                                        })}
                                    </div>
                                </div>
                            </div>
                            : <div className={classes.centeredRow}>

                                {/* Name */}
                                <FormControl className={classes.formControl}>
                                    <TextField variant="outlined" required value={name} onChange={handleChangeName}
                                               id="reg-playlist-name"
                                               label={intl.formatMessage({
                                                   id: 'app.playlists.edit.form.name.label',
                                                   defaultMessage: 'Name'
                                               })}

                                               InputProps={{
                                                   endAdornment: (
                                                       <InputAdornment position="end">
                                                           {association &&
                                                           <AssociationLogo association={association}
                                                                            width={35}
                                                                            height={35}/>}

                                                           <div className={classes.totalDuration}>
                                                               <div>
                                                                   {intl.formatMessage({
                                                                       id: 'app.playlists.edit.form.totalDuration',
                                                                       defaultMessage: 'Duration'
                                                                   })}:
                                                               </div>
                                                               <b>{getPlaylistDuration()}</b>
                                                           </div>
                                                       </InputAdornment>
                                                   )
                                               }}
                                    />
                                    <FormHelperText variant="outlined" error={!name} id="reg-playlist-name-help">
                                        {showIfTrue(intl.formatMessage({
                                            id: 'app.playlists.edit.form.name.help',
                                            defaultMessage: 'Enter name'
                                        }), !name)}
                                    </FormHelperText>
                                </FormControl>

                                {/* Feedback mode */}
                                <ToggleButtonGroup className={classes.toggleButtonContainer}
                                                   value={feedbackMode}
                                                   exclusive
                                                   onChange={handleChangeFeedbackMode}>

                                    <ToggleButton className={classes.toggleButton} title={intl.formatMessage({
                                        id: 'app.playlists.edit.form.feedbackMode.standard.label',
                                        defaultMessage: 'Standard'
                                    })} value="standard">
                                        <School/>
                                    </ToggleButton>

                                    <ToggleButton className={classes.toggleButton} title={intl.formatMessage({
                                        id: 'app.playlists.edit.form.feedbackMode.lab.label',
                                        defaultMessage: 'Lab'
                                    })} value="lab">
                                        <Science/>
                                    </ToggleButton>

                                    <ToggleButton className={classes.toggleButton} title={intl.formatMessage({
                                        id: 'app.playlists.edit.form.feedbackMode.exam.label',
                                        defaultMessage: 'Exam'
                                    })} value="exam">
                                        <FactCheck/>
                                    </ToggleButton>
                                </ToggleButtonGroup>


                            </div>}
                    </Grid>
                    <Grid item xs={6}>
                        {/* Available scored scenes */}
                        <Droppable droppableId="scoredScenesSource">
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef}
                                     className={classes.sourceList}>
                                    {getFilteredScoredScenesSource().map((scoredScene, index) => (
                                        <Draggable
                                            key={scoredScene.key}
                                            draggableId={scoredScene.key}
                                            index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}>
                                                    <ScoredSceneCardDetailed scoredScene={scoredScene}/>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Grid>
                    <Grid item xs={6}>
                        {/* Chosen scored scenes in the playlist */}
                        <Droppable droppableId="scoredScenesDestination">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    className={classes.destinationList}>
                                    {scoredScenesDestination.map((scoredScene, index) => (
                                        <Draggable
                                            key={scoredScene.key}
                                            draggableId={scoredScene.key}
                                            index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}>

                                                    <ScoredSceneCardDetailed scoredScene={scoredScene}/>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Grid>
                </Grid>
            </DragDropContext>
        </fieldset>
    );
}

export default GeneralEditPlaylistForm
